import { ArrowUp, ChevronDown, Globe, LoaderCircle, Search, type LucideIcon } from "lucide-react";
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import type { CopilotoMode } from "@/shared/api/api-client";
import { useQuotaCountdown } from "@/features/copiloto/CopilotoContext";

import { useDismiss } from "@/shared/hooks/useDismiss";
const MODE_OPTIONS: { value: CopilotoMode; label: string; icon: LucideIcon }[] = [
  { value: "padrao", label: "Padrão", icon: Search },
  { value: "web", label: "Web", icon: Globe }
];

function ModeDropdown({
  mode,
  onChange,
  disabled
}: {
  mode: CopilotoMode;
  onChange: (mode: CopilotoMode) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Fechava só no clique-fora, sem Esc — a metade que faltava era
  // acaso da cópia, não escolha: todo outro menu da interface fecha nos
  // dois. Ver `useDismiss`.
  useDismiss(open, () => setOpen(false), { ref });

  const current = MODE_OPTIONS.find(option => option.value === mode) ?? MODE_OPTIONS[0];

  return (
    <div className="copiloto-mode" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        className={open ? "is-open" : ""}
      >
        <span>{current.label}</span>
        <ChevronDown size={13} className={`copiloto-mode-chevron ${open ? "is-open" : ""}`} />
      </button>
      {open && (
        <div className="copiloto-mode-menu" role="menu">
          {MODE_OPTIONS.map(option => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                type="button"
                role="menuitem"
                className={option.value === mode ? "active" : ""}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <Icon size={16} />
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const COMPOSER_SINGLE_LINE_HEIGHT = 56;
const COMPOSER_MAX_HEIGHT = 180;

export function CopilotoComposer({
  mode,
  onModeChange,
  onSend,
  disabled,
  placeholder,
  hero = false,
  hideDisclaimer = false
}: {
  mode: CopilotoMode;
  onModeChange?: (mode: CopilotoMode) => void;
  onSend: (content: string) => void;
  disabled: boolean;
  placeholder?: string;
  hero?: boolean;
  hideDisclaimer?: boolean;
}) {
  const [message, setMessage] = useState("");
  const [isTall, setIsTall] = useState(false);
  // Cota estourada: a caixa TRAVA com o número caindo, em vez de só
  // mostrar o erro. Sem isso a pessoa clica de novo, e cada clique é
  // outro 429 — a recusa não consome vaga, mas a leitura da tela vira
  // "não funciona mais".
  const bloqueadoPor = useQuotaCountdown();
  const travado = disabled || bloqueadoPor !== null;
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resize = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    const scrollHeight = el.scrollHeight;
    el.style.height = `${Math.min(scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
    if (el.value.trim() === "") {
      setIsTall(false);
    } else if (scrollHeight > COMPOSER_SINGLE_LINE_HEIGHT) {
      setIsTall(true);
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      resize(textareaRef.current);
    }
  }, [message]);

  const submit = () => {
    if (!message.trim() || travado) return;
    onSend(message);
    setMessage("");
    setIsTall(false);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  return (
    <div className={`copiloto-composer-wrap ${hero ? "is-hero" : ""}`}>
      <form
        className={`copiloto-composer ${hero ? "is-hero" : ""} ${isTall ? "is-tall" : ""} ${isFocused ? "is-focused" : ""}`}
        onSubmit={handleSubmit}
      >
        <div className="copiloto-composer-text">
          <textarea
            ref={textareaRef}
            rows={1}
            value={message}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={event => setMessage(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              bloqueadoPor !== null
                ? `Limite atingido — aguarde ${bloqueadoPor}s`
                : (placeholder ??
                  (hero
                    ? "Pergunte alguma coisa…"
                    : "Pergunte algo à Mestra Help…"))
            }
            disabled={travado}
            aria-label="Pergunte algo à Mestra Help"
          />
        </div>
        <div className="copiloto-controls">
          {onModeChange && <ModeDropdown mode={mode} onChange={onModeChange} disabled={travado}/>}
          <div className={`copiloto-send-slot ${message.trim() ? "is-visible" : ""}`}>
            <button
              type="submit"
              className="copiloto-send"
              disabled={travado || !message.trim()}
              aria-label="Enviar mensagem"
            >
              {disabled ? <LoaderCircle className="spin" size={16}/> : <ArrowUp size={16}/>}
            </button>
          </div>
        </div>
      </form>
      {!hideDisclaimer && (
        <p className="copiloto-disclaimer">
          Este assistente é de uso interno da FSB Holding. Respostas de IA podem conter imprecisões.
        </p>
      )}
    </div>
  );
}
